'use client'

import { cn } from '@/lib/utils'

export interface TabItem {
  id: string
  label: string
  count?: number
  disabled?: boolean
}

interface TabsProps {
  tabs: TabItem[]
  activeTab: string
  onChange: (tabId: string) => void
  className?: string
  /** Visual style variant */
  variant?: 'default' | 'pills'
}

/**
 * Tabs Component
 * Reusable tab navigation for switching between views
 */
export function Tabs({
  tabs,
  activeTab,
  onChange,
  className,
  variant = 'default',
}: TabsProps) {
  if (variant === 'pills') {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1 p-1 bg-muted rounded-lg',
          className
        )}
        role="tablist"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-disabled={tab.disabled}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md transition-all duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              activeTab === tab.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
              tab.disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  'ml-2 text-xs',
                  activeTab === tab.id
                    ? 'text-muted-foreground'
                    : 'text-muted-foreground/70'
                )}
              >
                ({tab.count})
              </span>
            )}
          </button>
        ))}
      </div>
    )
  }

  // Default underline style
  return (
    <div className={cn('border-b border-border', className)} role="tablist">
      <div className="flex gap-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-disabled={tab.disabled}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative pb-3 text-sm font-medium transition-colors duration-200',
              'focus:outline-none focus-visible:text-primary',
              activeTab === tab.id
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground',
              tab.disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  'ml-1.5 text-xs',
                  activeTab === tab.id
                    ? 'text-muted-foreground'
                    : 'text-muted-foreground/70'
                )}
              >
                ({tab.count})
              </span>
            )}
            {/* Active indicator */}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
